import { useState } from "react";
import { wallSystemTypes } from "../../utils/wallTypes";
import { openingKindOf, suggestOpeningMark, openingRooms, openingAreaSf, openingWidthFt, openingHeightFt, DEDUCTION_POLICY_OPTIONS } from "../../config/openings";
import { parseGeom } from "../../config/scopeOptions";
import FeetInchesInput from "../../components/FeetInchesInput";
import { useUnitsPreference } from "../../utils/useUnits";

// Edit an already-placed condition's attributes (type/room/height override/
// quantity override/notes), or add a brand-new one with no drawn geometry at
// all — same flyout, two modes, mirroring RoomDetailFlyout.jsx's pattern for
// rooms. `condition` present = edit (per-field autosave, no separate Save
// button, same as RoomDetailFlyout); `condition` null = create (a local
// draft + one "+ Create Condition" button, since nothing meaningful exists
// until at least a type is picked).
const fld = { fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 3 };
const inp = { height: 32, width: "100%", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 13, color: "var(--text)", background: "var(--surface)", outline: "none", boxSizing: "border-box" };

export default function ConditionDetailFlyout({
  condition, conditionTypes = [], layers = [], rooms = [], isLocked,
  conditions = [], // the revision's drawn rows — for "Suggest" on an opening's mark (P1 Phase C)
  onSave,    // (condition, patch) -> edit mode
  onCreate,  // (draft) -> create mode
  onClose,
}) {
  const editing = !!condition;
  const [units] = useUnitsPreference();
  // A placed symbol (door, window, marker) keeps its width in its own geometry, set at placement from the type's default
  // width. Editing it redraws the symbol and feeds the schedule and any deduction (openingWidthFt reads it first).
  const symbolGeom = editing && condition.Style === "count" ? parseGeom(condition) : null;
  const hasSymbolWidth = !!symbolGeom && Number.isFinite(+symbolGeom.x) && Number.isFinite(+symbolGeom.y);
  // An auto-generated wall condition (see sp_SyncAutoWallConditions,
  // db/37_condition_library.sql) — its Room is system-derived from room
  // adjacency and gets overwritten by the next layout sync, so editing it
  // here would just be silently reverted; only a 'linear' type makes sense
  // for it (it's always a wall run, never an area fill or a placed count).
  const isAutoWall = !!condition?.TargetType?.startsWith("auto_wall_");
  // Walls only (feedback #25) — plus whatever this wall already has, so its current type never vanishes from the list.
  const pickableTypes = isAutoWall
    ? (() => { const walls = wallSystemTypes(conditionTypes, layers); const keep = new Set([typeKeyOf(condition), typeKeyOfFaceB(condition)]); return [...walls, ...conditionTypes.filter(t => t.Style === "linear" && keep.has(t.TypeKey) && !walls.includes(t))]; })()
    : conditionTypes;
  // TypeKey identifies a type across both catalogs (Condition Library
  // rows and drawable Items/Assemblies) without their numeric PKs
  // colliding — see RoomTakeoff.jsx's decodeTypeKey comment for the full
  // encoding. Built here off a drawn INSTANCE's own resolved Kind/ItemID/
  // AssemblyID/TakeoffConditionLibraryID (sp_GetTakeoffConditionsDrawn).
  function typeKeyOf(inst) {
    if (!inst) return null;
    if (inst.Kind === "item") return `item:${inst.ItemID}`;
    if (inst.Kind === "assembly") return `asm:${inst.AssemblyID}`;
    return inst.TakeoffConditionLibraryID != null ? `ct:${inst.TakeoffConditionLibraryID}` : null;
  }
  // Face B (Phase D-2, see project memory): same resolution as Face A,
  // just off the instance's *FaceB fields — Face B can now reference a
  // real Item/Assembly too, not just a Condition Library type, so both
  // pickers share the same pickableTypes list.
  function typeKeyOfFaceB(inst) {
    if (!inst) return null;
    if (inst.KindFaceB === "item") return `item:${inst.ItemIDFaceB}`;
    if (inst.KindFaceB === "assembly") return `asm:${inst.AssemblyIDFaceB}`;
    return inst.TakeoffConditionLibraryIDFaceB != null ? `ct:${inst.TakeoffConditionLibraryIDFaceB}` : null;
  }
  // Face B: a SHARED auto-wall segment (RoomBID set — see
  // TakeoffConditionsDrawn.TakeoffConditionLibraryIDFaceB in db/37_condition_library.sql)
  // is one physical wall bordering TWO rooms, each of which can carry its
  // own independently-assigned finish (e.g. liner on one room's face,
  // drywall on the other's). RoomID's own name/RoomBID's own name label the
  // two pickers directly (rather than a generic "Face A/B") since the same
  // corridor can be RoomID in one shared-wall row and RoomBID in the next —
  // a fixed A/B label would read inconsistently room to room.
  const hasFaceB = isAutoWall && condition?.RoomBID != null;
  const roomAName = hasFaceB ? rooms.find(r => r.RoomID === condition.RoomID)?.RoomName : null;
  const roomBName = hasFaceB ? rooms.find(r => r.RoomID === condition.RoomBID)?.RoomName : null;
  const [draft, setDraft] = useState(() => ({
    conditionTypeId: typeKeyOf(condition),
    conditionTypeIdFaceB: typeKeyOfFaceB(condition),
    roomId: condition?.RoomID ?? null,
    quantityOverride: condition?.QuantityOverride ?? "",
    heightFtOverride: condition?.HeightFtOverride ?? "",
    notes: condition?.Notes ?? "",
    mark: condition?.Mark ?? "",
    roomBId: condition?.RoomBID ?? null,
    deductionPolicy: condition?.DeductionPolicy ?? "inherit",
  }));
  // P1 Phase C — a placed opening (door / window / pass-through) carries a
  // Mark (its design-record identity) and an optional explicit "to" room;
  // the derived one is shown as the placeholder.
  const openingKind = editing ? openingKindOf(condition) : null;
  const derivedTo = openingKind && condition.RoomBID == null ? openingRooms(condition, rooms).to : null;

  const draftType = pickableTypes.find(t => t.TypeKey === draft.conditionTypeId);
  const style = editing ? condition.Style : draftType?.Style;
  const qtyLabel = style === "count" ? "Quantity (EA)" : "Quantity Override (LF)";
  const qtyPlaceholder = editing
    ? (style === "count" ? "1 per instance" : "— uses drawn length")
    : (style === "count" ? "e.g. 6" : "e.g. 42.5");
  const defaultHeight = editing ? condition.DefaultHeightFt : draftType?.DefaultHeightFt;

  function set(patch) {
    if (!editing) return;
    setDraft(d => ({ ...d, ...patch }));
    onSave(condition, patch);
  }

  function submitCreate() {
    if (!draft.conditionTypeId) return;
    onCreate({
      conditionTypeId: draft.conditionTypeId,
      roomId: draft.roomId,
      quantityOverride: draft.quantityOverride === "" ? null : +draft.quantityOverride,
      heightFtOverride: draft.heightFtOverride === "" ? null : +draft.heightFtOverride,
      notes: draft.notes.trim() || null,
    });
    onClose();
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 299 }} />
      <div style={{
        position: "fixed", top: 0, right: 0, width: 380, height: "100vh",
        background: "var(--surface)", borderLeft: "1px solid var(--cs-border)",
        boxShadow: "-4px 0 32px rgba(0,0,0,0.12)", zIndex: 300,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cs-border)", flexShrink: 0, background: "#0c4a6e", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
              {editing ? "📏 Condition Details" : "📏 Add Condition"}
            </div>
            <div style={{ fontSize: 12, color: "#93c5fd", marginTop: 3 }}>
              {editing ? (condition.Code || condition.Name) : "No drawn geometry — a manual count/length entry"}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "none", cursor: "pointer", fontSize: 18, color: "#fff", lineHeight: 1, padding: "4px 8px", borderRadius: 6 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            {!editing && (
              <div>
                <label style={fld}>Condition Type</label>
                <select
                  value={draft.conditionTypeId ?? ""}
                  disabled={isLocked}
                  onChange={e => setDraft(d => ({ ...d, conditionTypeId: e.target.value || null }))}
                  style={inp}
                >
                  <option value="">— pick a type —</option>
                  {pickableTypes.map(t => <option key={t.TypeKey} value={t.TypeKey}>{t.Name} ({t.CSICode})</option>)}
                </select>
              </div>
            )}
            {editing && (
              <div>
                <label style={fld}>{hasFaceB ? `Condition Type — ${roomAName || "this side"} face` : "Condition Type"}</label>
                <select
                  value={draft.conditionTypeId ?? ""}
                  disabled={isLocked}
                  onChange={e => set({ conditionTypeId: e.target.value || null })}
                  style={inp}
                >
                  {isAutoWall && <option value="">— pick a type —</option>}
                  {pickableTypes.map(t => <option key={t.TypeKey} value={t.TypeKey}>{t.Name} ({t.CSICode})</option>)}
                </select>
              </div>
            )}
            {editing && hasFaceB && (
              <div>
                <label style={fld}>{`Condition Type — ${roomBName || "other side"} face`}</label>
                <select
                  value={draft.conditionTypeIdFaceB ?? ""}
                  disabled={isLocked}
                  onChange={e => {
                    const conditionTypeIdFaceB = e.target.value || null;
                    setDraft(d => ({ ...d, conditionTypeIdFaceB }));
                    set({ conditionTypeIdFaceB });
                  }}
                  style={inp}
                >
                  <option value="">— same as {roomAName || "other side"} (symmetric) —</option>
                  {pickableTypes.map(t => <option key={t.TypeKey} value={t.TypeKey}>{t.Name} ({t.CSICode})</option>)}
                </select>
                <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 4 }}>
                  This wall is flush between two rooms — pick a different type here only if{" "}
                  {roomBName || "the other room"}'s face is finished differently than {roomAName || "this one"}'s.
                </div>
              </div>
            )}
            <div>
              <label style={fld}>Room{isAutoWall && " (auto — from layout)"}</label>
              <select
                value={draft.roomId ?? ""}
                disabled={isLocked || isAutoWall}
                onChange={e => {
                  const roomId = e.target.value ? +e.target.value : null;
                  setDraft(d => ({ ...d, roomId }));
                  if (editing) set({ roomId });
                }}
                style={inp}
              >
                <option value="">— unassigned —</option>
                {rooms.map(r => <option key={r.RoomID} value={r.RoomID}>{r.RoomName}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={fld}>{qtyLabel}</label>
                <input
                  type="number" min={0} step="any"
                  value={draft.quantityOverride}
                  disabled={isLocked}
                  placeholder={qtyPlaceholder}
                  style={{ ...inp, textAlign: "right" }}
                  onChange={e => setDraft(d => ({ ...d, quantityOverride: e.target.value }))}
                  onBlur={e => { if (editing) set({ quantityOverride: e.target.value === "" ? null : +e.target.value }); }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={fld}>Height Override (ft)</label>
                <input
                  type="number" min={0} step="any"
                  value={draft.heightFtOverride}
                  disabled={isLocked}
                  placeholder={defaultHeight != null ? `— uses ${defaultHeight} ft default` : "—"}
                  style={{ ...inp, textAlign: "right" }}
                  onChange={e => setDraft(d => ({ ...d, heightFtOverride: e.target.value }))}
                  onBlur={e => { if (editing) set({ heightFtOverride: e.target.value === "" ? null : +e.target.value }); }}
                />
              </div>
            </div>
            {hasSymbolWidth && (
              <div>
                <label style={fld}>Width</label>
                <FeetInchesInput
                  valueFt={symbolGeom.widthFt ?? condition.DefaultWidthFt ?? null}
                  units={units}
                  disabled={isLocked}
                  ariaLabel="Width"
                  placeholder={condition.DefaultWidthFt != null ? `— type default ${condition.DefaultWidthFt} ft` : "—"}
                  style={{ ...inp, textAlign: "right" }}
                  title={`Width of this ${openingKind ? openingKind.label.toLowerCase() : "symbol"}, e.g. 4' or 3'-4". Redraws it and feeds the schedule. Changing the Condition type resets it to the new type's default width.`}
                  onCommit={ft => { if (ft != null && ft > 0) set({ widthFt: ft }); else if (condition.DefaultWidthFt > 0) set({ widthFt: +condition.DefaultWidthFt }); }}
                />
              </div>
            )}
            {openingKind && (
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={fld}>{openingKind.label} mark</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      value={draft.mark}
                      disabled={isLocked}
                      placeholder="e.g. D-101-1"
                      title="This opening's identity across the canvas, schedules, interlocks and revisions. Suggested at placement; never renumbered automatically."
                      style={{ ...inp, flex: 1 }}
                      onChange={e => setDraft(d => ({ ...d, mark: e.target.value }))}
                      onBlur={e => { const v = e.target.value.trim() || null; if (v !== (condition.Mark ?? null)) set({ mark: v }); }}
                    />
                    {!isLocked && (
                      <button type="button" title="Next free mark for this room"
                        onClick={() => { const v = suggestOpeningMark(openingKind, rooms.find(r => r.RoomID === (draft.roomId ?? condition.RoomID)) || null, conditions.filter(c => c.TakeoffConditionDrawnID !== condition.TakeoffConditionDrawnID)); setDraft(d => ({ ...d, mark: v })); set({ mark: v }); }}
                        style={{ height: 34, padding: "0 10px", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "var(--surface2, #f8fafc)", cursor: "pointer", fontSize: 12 }}>
                        Suggest
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={fld}>To room</label>
                  <select
                    value={draft.roomBId ?? ""}
                    disabled={isLocked}
                    title="Blank = derived from the wall this symbol sits in (shown as the placeholder); pick a room to override."
                    style={inp}
                    onChange={e => { const roomBId = e.target.value ? +e.target.value : null; setDraft(d => ({ ...d, roomBId })); set({ roomBId }); }}
                  >
                    <option value="">{derivedTo ? `— from layout: ${derivedTo.RoomName} —` : "— from layout (none / exterior) —"}</option>
                    {rooms.filter(r => r.RoomID !== (draft.roomId ?? condition.RoomID)).map(r => <option key={r.RoomID} value={r.RoomID}>{r.RoomName}</option>)}
                  </select>
                </div>
              </div>
            )}
            {openingKind && (() => {
              // D-3: whether this opening deducts its width × height from the wall-area
              // element of each room it sits in. 'inherit' defers to that element's
              // own "Deduct openings" default; the SF is derived live, never stored.
              const sf = openingAreaSf(condition);
              return (
                <div>
                  <label style={fld}>Wall-area deduction</label>
                  <select
                    value={draft.deductionPolicy ?? "inherit"}
                    disabled={isLocked}
                    title="Applies to the wall-area Room Element of each room this opening sits in. Never a hard rule — 'inherit' follows that element's own default."
                    style={inp}
                    onChange={e => { const v = e.target.value; set({ deductionPolicy: v === "inherit" ? null : v }); }}
                  >
                    {DEDUCTION_POLICY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <div style={{ fontSize: 11, color: sf == null ? "#b45309" : "var(--text3)", marginTop: 3 }}>
                    {sf == null ? `⚠ No ${[openingWidthFt(condition) == null && "width", openingHeightFt(condition) == null && "height"].filter(Boolean).join(" or ")} on this opening — nothing can be deducted until ${openingWidthFt(condition) == null ? "the Width above is set" : "the type's default height or this opening's Height Override is set"}.` : `Deducts ${Math.round(sf * 10) / 10} SF per room wall it sits in when the policy resolves to deduct.`}
                  </div>
                </div>
              );
            })()}
            <div>
              <label style={fld}>Notes</label>
              <input
                value={draft.notes}
                disabled={isLocked}
                placeholder="—"
                style={inp}
                onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                onBlur={e => { if (editing) set({ notes: e.target.value.trim() || null }); }}
              />
            </div>
            {!editing && (
              <button
                onClick={submitCreate}
                disabled={isLocked || !draft.conditionTypeId}
                style={{
                  height: 34, border: "none", borderRadius: "var(--radius)", cursor: draft.conditionTypeId ? "pointer" : "default",
                  background: draft.conditionTypeId ? "var(--cs-blue)" : "var(--cs-border)", color: "#fff", fontSize: 13, fontWeight: 700,
                }}
              >
                + Create Condition
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
